import {
  And,
  ListSizeGreaterThan,
  Not,
  NumberEquals,
  StringContainsFold,
  StringEqualFold,
  StringEquals,
  StringListContains,
  StringListContainsAll,
  StringListContainsPrefix,
  StringListContainsPrefixAll,
  TimeAfter,
  TimeBefore,
} from '@vendasta/automata';
import { DynamicStringDisplayService, FreeTextInputComponent } from './dynamic-options';
import { AccountGroupLifecycleOptionsComponent } from './dynamic-options/account-group-lifecycle-options/account-group-lifecycle-options.component';
import { AccountGroupOriginOptionsDisplayService } from './dynamic-options/account-group-origin-options/account-group-origin-options-display.service';
import { AccountGroupOriginOptionsComponent } from './dynamic-options/account-group-origin-options/account-group-origin-options.component';
import { AccountGroupTagOptionsComponent } from './dynamic-options/account-group-tag-options/account-group-tag-options.component';
import { AccountOptionsDisplayService } from './dynamic-options/account-options/account-options-display.service';
import { AccountOptionsComponent } from './dynamic-options/account-options/account-options.component';
import { CategoriesOptionsDisplayService } from './dynamic-options/categories-options/categories-options-display.service';
import { CategoriesOptionsComponent } from './dynamic-options/categories-options/categories-options.component';
import { DateInputDisplayService } from './dynamic-options/date-input/date-input-display.service';
import { DateInputComponent } from './dynamic-options/date-input/date-input.component';
import { EmptyNumberComponent, EmptyNumberDisplayService } from './dynamic-options/empty-number-component';
import { EmptyStringComponent } from './dynamic-options/empty-string-component';
import { LifecycleOptionsDisplayService } from './dynamic-options/lifecycle-options/lifecycle-options-display.service';
import { ListOptionsDisplayService } from './dynamic-options/list-options/list-options-display.service';
import { ListOptionsComponent } from './dynamic-options/list-options/list-options.component';
import { LocationOptionsDisplayService } from './dynamic-options/location-options/location-options-display.service';
import { LocationOptionsComponent } from './dynamic-options/location-options/location-options.component';
import { MarketOptionsDisplayService } from './dynamic-options/market-options/market-options-display.service';
import { MarketOptionsComponent } from './dynamic-options/market-options/market-options.component';
import { SalesPersonOptionsDisplayService } from './dynamic-options/sales-person-options/sales-person-options-display.service';
import { SalesPersonOptionsComponent } from './dynamic-options/sales-person-options/sales-person-options.component';
import { SalesTeamOptionsComponent } from './dynamic-options/sales-team-options/sales-team-options.component';
import { TagOptionsDisplayService } from './dynamic-options/tag-options/tag-options-display.service';
import { DataFilter } from './interface';

// This key is not used for comparisons since this filter is composed of a multi-valued AND rule
const LOCATION =
  '{.account_group.nap_data.country}{.account_group.nap_data.state}{.account_group.nap_data.city}{.account_group.nap_data.zip}';
// Rules have to be pre-defined due to the way that AND rules work when calling setValue.
const LOCATION_CHILD_RULES = [
  new StringEquals('{.account_group.nap_data.country}', '', ''),
  new StringEquals('{.account_group.nap_data.state}', '', ''),
  new StringEqualFold('{.account_group.nap_data.city}', '', ''),
  new StringEqualFold('{.account_group.nap_data.zip}', '', ''),
];
export class LocationDataFilter implements DataFilter {
  rules = [new And(LOCATION_CHILD_RULES), new Not(new And(LOCATION_CHILD_RULES))];
  dataEntryComponent = LocationOptionsComponent;
  displayService = LocationOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.DATA_LABELS.LOCATION';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.NOT_EQUALS',
  ];
}

const WEBSITE = '{.account_group.nap_data.website}';
export class WebsiteDataFilter implements DataFilter {
  rules = [new Not(new StringEquals(WEBSITE)), new StringEquals(WEBSITE), new StringContainsFold(WEBSITE)];
  dataEntryComponentList = [EmptyStringComponent, EmptyStringComponent, FreeTextInputComponent];
  displayService = DynamicStringDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.DATA_LABELS.WEBSITE';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.EXISTS',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_EXIST',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.CONTAINS',
  ];
}

const LIFECYCLE_STAGE = `{.account_group.marketing_info.lifecycle_stage}`;
export class LifecycleStageFilter implements DataFilter {
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.NOT_EQUALS',
  ];
  displayService = LifecycleOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.DATA_LABELS.LIFECYCLE_STAGE';
  rules = [new NumberEquals(LIFECYCLE_STAGE), new Not(new NumberEquals(LIFECYCLE_STAGE))];
  dataEntryComponent = AccountGroupLifecycleOptionsComponent;
}

const HOURS_OF_OPERATION = `{.account_group.hours_of_operation[*]}`;
export class HoursOfOperationFilter implements DataFilter {
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.EXISTS',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_EXIST',
  ];
  displayService = EmptyNumberDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.DATA_LABELS.HOURS_OF_OPERATION';
  rules = [new ListSizeGreaterThan(HOURS_OF_OPERATION), new Not(new ListSizeGreaterThan(HOURS_OF_OPERATION))];
  dataEntryComponent = EmptyNumberComponent;
}

const TAGS = '{.account_group.external_identifiers.tags}';
export class TagDataFilter implements DataFilter {
  rules = [
    new StringListContains(TAGS),
    new StringListContainsAll(TAGS),
    new Not(new StringListContains(TAGS)),
    new Not(new StringListContainsAll(TAGS)),
  ];
  dataEntryComponent = AccountGroupTagOptionsComponent;
  displayService = TagOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.DATA_LABELS.TAGS';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.INCLUDES_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.INCLUDES_ALL',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_INCLUDE_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_INCLUDE_ALL',
  ];
}

const ACCOUNTS = '{.account_group.account_group.accounts[*].marketplace_app_id}';
export class AccountDataFilter implements DataFilter {
  rules = [
    new StringListContains(ACCOUNTS),
    new StringListContainsAll(ACCOUNTS),
    new Not(new StringListContains(ACCOUNTS)),
    new Not(new StringListContainsAll(ACCOUNTS)),
  ];
  dataEntryComponent = AccountOptionsComponent;
  displayService = AccountOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.DATA_LABELS.ACCOUNTS';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.INCLUDES_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.INCLUDES_ALL',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_INCLUDE_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_INCLUDE_ALL',
  ];
}

const CATEGORIES = '{.account_group.external_identifiers.tax_ids}';
export class CategoriesDataFilter implements DataFilter {
  rules = [
    new StringListContainsPrefix(CATEGORIES),
    new StringListContainsPrefixAll(CATEGORIES),
    new Not(new StringListContainsPrefix(CATEGORIES)),
    new Not(new StringListContainsPrefixAll(CATEGORIES)),
  ];
  dataEntryComponent = CategoriesOptionsComponent;
  displayService = CategoriesOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.DATA_LABELS.CATEGORIES';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.INCLUDES_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.INCLUDES_ALL',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_INCLUDE_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_INCLUDE_ALL',
  ];
}

const ASSIGNED_SALES_PERSON = '{.extra_data.sales_person_unified_user_id}';
const SALES_TEAM = '{.extra_data.assigned_salesperson_sales_team_ids}';
export class AssignedSalesPersonFilter implements DataFilter {
  rules = [
    new Not(new StringEquals(ASSIGNED_SALES_PERSON)),
    new StringEquals(ASSIGNED_SALES_PERSON),
    new StringListContains(ASSIGNED_SALES_PERSON),
    new Not(new StringListContains(ASSIGNED_SALES_PERSON)),
    new StringListContains(SALES_TEAM),
    new Not(new StringListContains(SALES_TEAM)),
  ];
  dataEntryComponentList = [
    EmptyStringComponent,
    EmptyStringComponent,
    SalesPersonOptionsComponent,
    SalesPersonOptionsComponent,
    SalesTeamOptionsComponent,
    SalesTeamOptionsComponent,
  ];
  displayService = SalesPersonOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.DATA_LABELS.ASSIGNED_SALES_PERSON';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.EXISTS',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_EXIST',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.IS_ONE_OF',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.IS_NOT_ONE_OF',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.IS_TEAM_MEMBER_OF_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.IS_NOT_TEAM_MEMBER_OF_ANY',
  ];
}

const ADDITIONAL_ASSIGNEES = '{.extra_data.additional_assignees_unified_user_id}';
export class AdditionalAssigneesDataFilter implements DataFilter {
  rules = [
    new StringListContains(ADDITIONAL_ASSIGNEES),
    new StringListContainsAll(ADDITIONAL_ASSIGNEES),
    new Not(new StringListContains(ADDITIONAL_ASSIGNEES)),
    new Not(new StringListContainsAll(ADDITIONAL_ASSIGNEES)),
  ];
  dataEntryComponentList = [
    SalesPersonOptionsComponent,
    SalesPersonOptionsComponent,
    SalesPersonOptionsComponent,
    SalesPersonOptionsComponent,
  ];
  displayService = SalesPersonOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.DATA_LABELS.ADDITIONAL_ASSIGNEES';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.INCLUDES_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.INCLUDES_ALL',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_INCLUDE_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_INCLUDE_ALL',
  ];
}

/*
 * Account Origin Filter
 * Uses StringListContains but data entry component is using just a simple select for now due to only one option existing.
 * When more are added, this should be changed to a multi-select.
 * */
const ACCOUNT_ORIGIN = '{.account_group.external_identifiers.origin}';
export class AccountOriginFilter implements DataFilter {
  rules = [
    new Not(new StringEquals(ACCOUNT_ORIGIN)),
    new StringEquals(ACCOUNT_ORIGIN),
    new StringListContains(ACCOUNT_ORIGIN),
    new Not(new StringListContains(ACCOUNT_ORIGIN)),
  ];
  dataEntryComponentList = [
    EmptyStringComponent,
    EmptyStringComponent,
    AccountGroupOriginOptionsComponent,
    AccountGroupOriginOptionsComponent,
  ];
  displayService = AccountGroupOriginOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.DATA_LABELS.ACCOUNT_ORIGIN';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.EXISTS',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_EXIST',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.NOT_EQUALS',
  ];
}

const CREATED_DATE = '{.account_group.account_group.created}';
export class CreatedDateDataFilter implements DataFilter {
  rules = [new TimeAfter(CREATED_DATE), new TimeBefore(CREATED_DATE)];
  dataEntryComponent = DateInputComponent;
  displayService = DateInputDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.DATA_LABELS.CREATED_DATE';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.AFTER',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.BEFORE',
  ];
}

const LISTS = '{.account_group.external_identifiers.action_lists}';
export class ListDataFilter implements DataFilter {
  rules = [
    new StringListContains(LISTS),
    new StringListContainsAll(LISTS),
    new Not(new StringListContains(LISTS)),
    new Not(new StringListContainsAll(LISTS)),
  ];
  dataEntryComponent = ListOptionsComponent;
  displayService = ListOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.DATA_LABELS.LISTS';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.INCLUDES_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.INCLUDES_ALL',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_INCLUDE_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_INCLUDE_ALL',
  ];
}

const MARKETS = '{.account_group.external_identifiers.market_id}';
export class MarketDataFilter implements DataFilter {
  rules = [new StringEquals(MARKETS), new Not(new StringEquals(MARKETS))];
  dataEntryComponent = MarketOptionsComponent;
  displayService = MarketOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.DATA_LABELS.MARKET';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.NOT_EQUALS',
  ];
}

const ACCOUNT_EMAIL = '{.account_group.legacy_product_details.email}';
export class AccountEmailFilter implements DataFilter {
  rules = [
    new StringEquals(ACCOUNT_EMAIL),
    new Not(new StringEquals(ACCOUNT_EMAIL)),
    new StringContainsFold(ACCOUNT_EMAIL),
  ];
  dataEntryComponentList = [EmptyStringComponent, EmptyStringComponent, FreeTextInputComponent];
  displayService = DynamicStringDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.DATA_LABELS.ACCOUNT_EMAIL';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.EXISTS',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_EXIST',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.CONTAINS',
  ];
}

export const ACCOUNT_DATA_COMPONENTS: Map<string, DataFilter> = new Map<string, DataFilter>([
  [LOCATION, new LocationDataFilter()],
  [WEBSITE, new WebsiteDataFilter()],
  [TAGS, new TagDataFilter()],
  [LISTS, new ListDataFilter()],
  [ACCOUNTS, new AccountDataFilter()],
  [CATEGORIES, new CategoriesDataFilter()],
  [ASSIGNED_SALES_PERSON, new AssignedSalesPersonFilter()],
  [ADDITIONAL_ASSIGNEES, new AdditionalAssigneesDataFilter()],
  [ACCOUNT_ORIGIN, new AccountOriginFilter()],
  [CREATED_DATE, new CreatedDateDataFilter()],
  [LIFECYCLE_STAGE, new LifecycleStageFilter()],
  [HOURS_OF_OPERATION, new HoursOfOperationFilter()],
  [SALES_TEAM, new AssignedSalesPersonFilter()],
  [MARKETS, new MarketDataFilter()],
  [ACCOUNT_EMAIL, new AccountEmailFilter()],
]);
